import { useMutation } from '@apollo/client';
import { useCallback, useState } from 'react';

import { linkInspectionServiceRequestMutation } from 'graphql-queries';

import { safeConvertToUUID } from 'uuid-encoding';

export class LinkServiceRequestError extends Error {}

export interface LinkServiceRequestInput {
  channelId: string;
  instanceId: string;
  itemId: string;
  serviceRequestId: string;
  linkedAt: Date;
}

type LinkServiceRequestMutationResult = {
  linkInspectionServiceRequest: {
    success: boolean;
    newVersion: number;
  } | null;
};

type LinkServiceRequestMutationVariables = {
  input: {
    channelId: string;
    instanceId: string;
    itemId: string;
    serviceRequestId: string;
    linkedAt: string;
  };
};

export interface UseLinkServiceRequestResult {
  linkServiceRequest: (input: LinkServiceRequestInput) => Promise<void>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * Hook to link a service request to an inspection item via the API.
 * Wraps the linkInspectionServiceRequest GraphQL mutation.
 */
export function useLinkServiceRequest(): UseLinkServiceRequestResult {
  const [mutate] = useMutation<
    LinkServiceRequestMutationResult,
    LinkServiceRequestMutationVariables
  >(linkInspectionServiceRequestMutation);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>();

  const linkServiceRequest = useCallback(
    async (input: LinkServiceRequestInput) => {
      setLoading(true);
      setError(undefined);

      const itemIdInUUID = safeConvertToUUID(input.itemId);

      try {
        const result = await mutate({
          variables: {
            input: {
              channelId: input.channelId,
              instanceId: input.instanceId,
              itemId: itemIdInUUID,
              serviceRequestId: input.serviceRequestId,
              linkedAt: input.linkedAt.toISOString(),
            },
          },
        });

        if (!result.data?.linkInspectionServiceRequest?.success) {
          throw new LinkServiceRequestError('Failed to link service request');
        }
      } catch (err) {
        const wrappedError =
          err instanceof LinkServiceRequestError
            ? err
            : new LinkServiceRequestError(
                err instanceof Error
                  ? err.message
                  : 'Failed to link service request'
              );

        setError(wrappedError);

        throw wrappedError;
      } finally {
        setLoading(false);
      }
    },
    [mutate]
  );

  return {
    linkServiceRequest,
    loading,
    error,
  };
}
